'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import { GravePlot } from './GravePlotContent';

interface TransferForm {
  newOwnerName: string;
  newOwnerIdNumber: string;
  newOwnerPhone: string;
  newOwnerEmail: string;
  newOwnerAddress: string;
  transferReason: string;
  transferDate: string;
}

export default function PlotTransferModal({ plot, onClose }: { plot: GravePlot; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<TransferForm>();

  const onSubmit = async () => {
    setLoading(true);
    // TODO: BACKEND — POST /api/plots/{plot.id}/transfer
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    toast.success(`Plot ${plot.plotId} ownership transferred successfully`);
    onClose();
  };

  return (
    <Modal open onClose={onClose} title="Transfer Plot Ownership" subtitle={`Plot ${plot.plotId} — ${plot.cemetery}`} size="md">
      <div className="space-y-5">
        {/* Current Owner */}
        <div className="p-3 bg-muted/40 rounded-lg">
          <p className="text-xs font-600 text-muted-foreground mb-2">Current Owner</p>
          <p className="text-sm font-600 text-foreground">{plot.ownerName}</p>
          <p className="text-xs text-muted-foreground">{plot.ownerContact}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label-base">New Owner Full Name <span className="text-negative">*</span></label>
            <input {...register('newOwnerName', { required: 'Required' })} className="input-base" />
            {errors.newOwnerName && <p className="text-xs text-negative mt-1">{errors.newOwnerName.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-base">ID Number <span className="text-negative">*</span></label>
              <input {...register('newOwnerIdNumber', { required: 'Required' })} className="input-base font-mono-data" maxLength={13} />
              {errors.newOwnerIdNumber && <p className="text-xs text-negative mt-1">{errors.newOwnerIdNumber.message}</p>}
            </div>
            <div>
              <label className="label-base">Phone Number <span className="text-negative">*</span></label>
              <input type="tel" {...register('newOwnerPhone', { required: 'Required' })} className="input-base" />
              {errors.newOwnerPhone && <p className="text-xs text-negative mt-1">{errors.newOwnerPhone.message}</p>}
            </div>
          </div>
          <div>
            <label className="label-base">Email Address</label>
            <input type="email" {...register('newOwnerEmail')} className="input-base" />
          </div>
          <div>
            <label className="label-base">Residential Address</label>
            <textarea {...register('newOwnerAddress')} rows={2} className="input-base resize-none" />
          </div>
          <div>
            <label className="label-base">Reason for Transfer <span className="text-negative">*</span></label>
            <select {...register('transferReason', { required: 'Required' })} className="input-base">
              <option value="">Select reason</option>
              {['Deceased estate transfer', 'Sale of rights', 'Court order', 'Family arrangement', 'Other'].map((r) => (
                <option key={`tr-${r}`} value={r}>{r}</option>
              ))}
            </select>
            {errors.transferReason && <p className="text-xs text-negative mt-1">{errors.transferReason.message}</p>}
          </div>
          <div>
            <label className="label-base">Transfer Date <span className="text-negative">*</span></label>
            <input type="date" {...register('transferDate', { required: 'Required' })} className="input-base" />
            {errors.transferDate && <p className="text-xs text-negative mt-1">{errors.transferDate.message}</p>}
          </div>

          <div className="flex gap-3 pt-2 border-t border-border">
            <button type="submit" disabled={loading} className="btn-primary flex-1 justify-center">
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : 'Confirm Transfer'}
            </button>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </div>
    </Modal>
  );
}